import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";

/**
 * Store the browser's timezone for a signed-in user who has none recorded.
 *
 * The login form reports the browser timezone, but only the paths that reach
 * `get_or_create_user` in the same request keep it. A user who arrives some other way —
 * IdP-initiated SSO (which never touches our login form at all), a direct /auth/sso/login
 * link, the Teams magic link, or simply an account created before any of this existed —
 * lands with `timezone` unset, and their nudges then fall back to the region default
 * rather than their actual local time. Running once per page load, after auth has already
 * completed, closes that gap for every entry point at once (ENG-1692).
 *
 * The `userDetails.timezone` check is an optimization, not the safety rule: these props are
 * from the initial render and another tab may have set a timezone since. The endpoint fills
 * a blank only, enforced in the UPDATE itself, so a deliberate choice wins the race.
 */
export async function backfillBrowserTimezone(userDetails: UserDetails | undefined | null): Promise<boolean> {
    if (!userDetails || userDetails.is_anonymous || userDetails.timezone) {
        return false;
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timezone) {
        return false;
    }

    // Best effort. The value is a convenience we can re-derive on the next page load, so a
    // failure here must never surface to the user or block the app from finishing its mount.
    const { execute, error, data } = useFetch("/api/accounts/user/timezone-backfill", { immediate: false }).post({ timezone }).json();
    await execute();
    return !error.value && Boolean(data.value?.saved);
}
